import { Checklist } from './Checklist';
import { ChecklistDTO, ChecklistItemDTO, FixedSectionsDTO } from './ChecklistDTO';
import { fetchPhotoSessionDetail } from '../../api/photo-session';
import { isComplementEditedByUser } from '../../utils/houseComplement';

type DynamicSectionType = 'house' | 'externalArea' | 'generalConditions';

type PhotoSessionProperty = {
  neighborhood?: string;
  address?: string;
  number?: string;
  numberOrBlock?: string;
  floor?: number;
  complement?: string;
};

type PhotoSessionDetail = {
  job?: {
    property?: PhotoSessionProperty;
  };
  property?: PhotoSessionProperty;
};

export class ChecklistMapper {
  private static hasItemValue(value: unknown): boolean {
    return value !== undefined && value !== null && value !== '';
  }

  private static mapDynamicSection({
    checklist,
    sectionType,
  }: {
    checklist: Checklist,
    sectionType: DynamicSectionType
  }): ChecklistItemDTO[] {
    const mappedItems: ChecklistItemDTO[] = [];
    const section = checklist[sectionType];

    if (!section) {
      return mappedItems;
    }

    Object.keys(section).forEach((sectionSlug) => {
      const items = section[sectionSlug]?.items;

      if (!items) {
        return;
      }

      Object.keys(items).forEach((itemSlug) => {
        const record = items[itemSlug];

        if (!record) {
          return;
        }

        const itemValue = record.type === 'integer' && record.optional
          ? record.value || 0
          : record.value;

        if (!this.hasItemValue(itemValue)) {
          return;
        }

        mappedItems.push({
          sectionSlug,
          sectionType,
          slug: itemSlug,
          value: itemValue,
        });
      });
    });

    return mappedItems;
  }

  private static mapDynamicItems({ checklist }: { checklist: Checklist }): ChecklistItemDTO[] {
    const houseSectionItems = this.mapDynamicSection({ checklist, sectionType: 'house' });
    const externalAreaSectionItems = this.mapDynamicSection({
      checklist,
      sectionType: 'externalArea',
    });
    const generalConditionsSectionItems = this.mapDynamicSection({
      checklist,
      sectionType: 'generalConditions',
    });

    return [
      ...houseSectionItems,
      ...externalAreaSectionItems,
      ...generalConditionsSectionItems,
    ];
  }

  private static formatContactPhoneNumber({
    areaCode,
    phoneNumber,
  }: {
    areaCode?: string;
    phoneNumber?: string;
  }): string | undefined {
    if (!this.hasItemValue(phoneNumber)) {
      return undefined;
    }

    return `(${areaCode || ''}) ${phoneNumber}`;
  }

  private static mapContacts({ checklist }: { checklist: Checklist }): FixedSectionsDTO['contacts'] {
    const contactItems = checklist.fixed?.contacts?.items;
    const name = contactItems?.name?.value;
    const type = contactItems?.type?.value;
    const phoneNumber = this.formatContactPhoneNumber({
      areaCode: contactItems?.areaCode?.value,
      phoneNumber: contactItems?.phoneNumber?.value,
    });

    if (
      !this.hasItemValue(name)
      && !this.hasItemValue(type)
      && !this.hasItemValue(phoneNumber)
    ) {
      return [];
    }

    return [{
      name: this.hasItemValue(name) ? String(name) : undefined,
      phoneNumber,
      type: this.hasItemValue(type) ? String(type) : undefined,
    }];
  }

  private static getPhotoSessionProperty(
    photoSessionData?: PhotoSessionDetail
  ): PhotoSessionProperty | undefined {
    return photoSessionData?.job?.property || photoSessionData?.property;
  }

  private static fillMissingAddressFromProperty({
    neighborhood,
    street,
    number,
    floor,
    complement,
    property,
    shouldFillComplement,
  }: {
    neighborhood?: string,
    street?: string,
    number?: string,
    floor?: number,
    complement?: string,
    property?: PhotoSessionProperty,
    shouldFillComplement: boolean,
  }) {
    if (!property) {
      return { neighborhood, street, number, floor, complement };
    }

    return {
      neighborhood: this.hasItemValue(neighborhood)
        ? neighborhood
        : property.neighborhood,
      street: this.hasItemValue(street)
        ? street
        : property.address,
      number: this.hasItemValue(number)
        ? number
        : property.numberOrBlock || property.number,
      floor: this.hasItemValue(floor)
        ? floor
        : property.floor,
      complement: shouldFillComplement && !this.hasItemValue(complement)
        ? property.complement
        : complement,
    };
  }

  private static needsAddressFallbackFromNetwork({
    neighborhood,
    street,
    number,
    complement,
    shouldFetchComplementFallback,
  }: {
    neighborhood?: string,
    street?: string,
    number?: string,
    complement?: string,
    shouldFetchComplementFallback: boolean,
  }): boolean {
    return (
      !this.hasItemValue(neighborhood)
      || !this.hasItemValue(street)
      || !this.hasItemValue(number)
      || (shouldFetchComplementFallback && !this.hasItemValue(complement))
    );
  }

  private static async mapFixedItems({
    checklist,
    photoSessionId,
    cachedSessionProperty,
  }: {
    checklist: Checklist,
    photoSessionId?: number,
    cachedSessionProperty?: PhotoSessionProperty,
  }): Promise<FixedSectionsDTO> {
    const houseItems = checklist.fixed?.house?.items;
    const complementEditedByUser = isComplementEditedByUser(houseItems);
    let neighborhood = houseItems?.neighborhood?.value;
    let street = houseItems?.street?.value;
    let number = houseItems?.number?.value;
    let floor = houseItems?.floor?.value;
    let complement = houseItems?.complement?.value;

    const shouldFetchComplementFallback =
      !complementEditedByUser && !this.hasItemValue(complement);

    const addressFromCache = this.fillMissingAddressFromProperty({
      neighborhood,
      street,
      number,
      floor,
      complement,
      property: cachedSessionProperty,
      shouldFillComplement: shouldFetchComplementFallback,
    });
    neighborhood = addressFromCache.neighborhood;
    street = addressFromCache.street;
    number = addressFromCache.number;
    floor = addressFromCache.floor;
    complement = addressFromCache.complement;

    const shouldFetchAddressFallback = photoSessionId
      && this.needsAddressFallbackFromNetwork({
        neighborhood,
        street,
        number,
        complement,
        shouldFetchComplementFallback,
      });

    if (shouldFetchAddressFallback) {
      try {
        const photoSessionData = await fetchPhotoSessionDetail(photoSessionId);
        const property = this.getPhotoSessionProperty(photoSessionData);
        const addressFromNetwork = this.fillMissingAddressFromProperty({
          neighborhood,
          street,
          number,
          floor,
          complement,
          property,
          shouldFillComplement: shouldFetchComplementFallback,
        });
        neighborhood = addressFromNetwork.neighborhood;
        street = addressFromNetwork.street;
        number = addressFromNetwork.number;
        floor = addressFromNetwork.floor;
        complement = addressFromNetwork.complement;
      } catch (error) {
        // Keep checklist values when the session fallback is unavailable.
      }
    }

    return {
      house: {
        complement,
        condoName: houseItems?.condoName?.value,
        floor,
        number,
        neighborhood,
        street,
        type: houseItems?.tipo?.value,
      },
      photoSession: {
        notes: checklist.fixed?.photoSession?.items?.photoSessionNotes?.value,
      },
      contacts: this.mapContacts({ checklist }),
    };
  }

  static async fromStore({
    checklist,
    photoSessionId,
    cachedSessionProperty,
  }: {
    checklist: Checklist,
    photoSessionId?: number,
    cachedSessionProperty?: PhotoSessionProperty,
  }): Promise<ChecklistDTO> {
    return {
      items: this.mapDynamicItems({ checklist }),
      fixedSections: await this.mapFixedItems({
        checklist,
        photoSessionId,
        cachedSessionProperty,
      }),
    };
  }
}
